import React, { useCallback, useEffect, useRef } from 'react';
import { Button, ButtonText, VStack } from '@gluestack-ui/themed';
import { useVideoPlayer, VideoView } from 'expo-video';
import { useTranslation } from 'react-i18next';

type LobbyOrientationPlayerProps = {
  videoUrl: string;
  onComplete: () => void;
  isCompleting?: boolean;
  skipDisabled?: boolean;
  paused?: boolean;
  startPositionSeconds?: number | null;
  onPauseOffset?: (seconds: number) => void;
  hideSkip?: boolean;
  skipLabelKey?: string;
};

/**
 * Skippable dry orientation film for cohort waiting beat B.
 */
export function LobbyOrientationPlayer({
  videoUrl,
  onComplete,
  isCompleting = false,
  skipDisabled = false,
  paused = false,
  startPositionSeconds = null,
  onPauseOffset,
  hideSkip = false,
  skipLabelKey = 'oralExam.lobby.waiting.orientationSkip',
}: LobbyOrientationPlayerProps) {
  const { t } = useTranslation();
  const resumeAppliedRef = useRef(false);
  const wasPausedRef = useRef(false);
  const player = useVideoPlayer(videoUrl, (setupPlayer) => {
    // expo-video setup callback owns this instance; configure in place.
    const instance = setupPlayer as { loop: boolean; play: () => void };
    instance.loop = false;
    if (!paused) {
      instance.play();
    }
  });

  useEffect(() => {
    const sub = player.addListener('playToEnd', () => {
      onComplete();
    });
    return () => {
      sub.remove();
    };
  }, [onComplete, player]);

  useEffect(() => {
    if (paused && !wasPausedRef.current) {
      onPauseOffset?.(player.currentTime);
    }
    wasPausedRef.current = paused;
    if (paused) {
      player.pause();
      return;
    }
    player.play();
  }, [onPauseOffset, paused, player]);

  useEffect(() => {
    resumeAppliedRef.current = false;
  }, [videoUrl]);

  useEffect(() => {
    if (startPositionSeconds == null || resumeAppliedRef.current) return;
    player.currentTime = startPositionSeconds;
    resumeAppliedRef.current = true;
    if (!paused) {
      player.play();
    }
  }, [paused, player, startPositionSeconds]);

  const handleSkip = useCallback(() => {
    player.pause();
    onComplete();
  }, [onComplete, player]);

  return (
    <VStack space="md" width="100%" maxWidth={560} testID="oral-exam-orientation-player">
      <VideoView
        player={player}
        style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 8 }}
        nativeControls
        contentFit="contain"
        accessibilityLabel={t('oralExam.lobby.orientation.title')}
      />
      {hideSkip ? null : (
        <Button
          variant="outline"
          size="sm"
          alignSelf="flex-end"
          onPress={handleSkip}
          isDisabled={isCompleting || skipDisabled}
          testID="oral-exam-orientation-skip"
        >
          <ButtonText>{t(skipLabelKey)}</ButtonText>
        </Button>
      )}
    </VStack>
  );
}
